import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import {
  paymentMethod as PaymentMethods,
  paymentType as PaymentTypes,
  standaloneRentersStates,
  policyType as PolicyType,
  defaultRenewalBillingDayStates,
  daysBeforeEffectiveDateForRenewalBilling
} from '@ourbranch/lookups';

import { brandConstants, brandConfig } from 'brand-config';
import { Label } from 'core';
import { Card } from 'core/components/card';
import { FormField } from 'core/components/form';
import Section from 'core/components/section';
import { CreditCardFeeDisclaimer } from 'common/components/payment/credit-card-fee-disclaimer';
import useDetailedPrice from 'offer/hooks/use-detailed-price';
import usePlanPrice from 'offer/hooks/use-plan-price';
import { getPolicyType } from 'core/helpers/policy-type';
import { nthDayFormatter, currencyFormatter, awsDateToStringDate, dateFormatter } from 'core/helpers/formatters';
import useCheckoutContext from '../../hooks/use-checkout-context';

import useStyles from './payments-disclosures.styles';

const paymentFrequencyLookup = {
  [PaymentTypes.QuarterTerm]: 'every 3 months',
  [PaymentTypes.HalfTerm]: 'every 6 months',
  [PaymentTypes.Monthly]: 'of each month'
};

const paymentNameLookup = {
  [PaymentTypes.QuarterTerm]: 'quarterly',
  [PaymentTypes.HalfTerm]: '6-month',
  [PaymentTypes.Monthly]: 'monthly'
};

// Forreston requested cleaner CC/ACH disclosure wording in issue #30791; CFM opts into the same
// wording for parity. Other brands keep the legacy copy until they opt in.
const useUpdatedDisclosureCopy = ['Forreston', 'CFM'].includes(brandConstants.SHORT_NAME);

const MortgageDisclosure = ({ mortgageDetails, amount }) => (
  <>
    invoice your mortgage lender <strong>{mortgageDetails.mortgageHolderName}</strong> in the amount of{' '}
    <strong>{amount}</strong> for your first policy term premium, and for the full premium of each future renewal term.
    You also authorize {brandConstants.FULL_NAME} to share information about your policy with your mortgage company as
    needed. <br />
    <br />
    If your mortgage company fails to pay, you will be responsible for payment.
  </>
);

MortgageDisclosure.propTypes = {
  mortgageDetails: PropTypes.object.isRequired,
  amount: PropTypes.string.isRequired
};

const CreditCardDisclosure = ({
  paymentType,
  cardLast4,
  amount,
  transactionFee,
  billingDayOfMonth,
  remainingPaymentsAmount,
  remainingPaymentsTransactionFee,
  remainingPaymentsTotal,
  megaDownPay,
  schedule,
  canBindDirectly,
  daysBeforeEffectiveDateForRenewalBilling
}) => {
  const creditCardInfo = !canBindDirectly
    ? 'credit card '
    : cardLast4
      ? `credit card ending in ${cardLast4} `
      : '(Please fill credit card info) ';

  const payingInInstallments = [PaymentTypes.QuarterTerm, PaymentTypes.HalfTerm, PaymentTypes.Monthly].includes(
    paymentType
  );
  const showCcFeeBreakdown = brandConfig.supportedFees?.creditCard && !!transactionFee;

  if (useUpdatedDisclosureCopy) {
    return (
      <>
        charge your <strong>{creditCardInfo}</strong>
        for{' '}
        <strong>
          {amount}
          {showCcFeeBreakdown && (
            <>
              {' plus a '}
              {transactionFee}
              {'* CC fee'}
            </>
          )}{' '}
          today
        </strong>{' '}
        {payingInInstallments && (
          <>
            for the initial payment of your policy, and{' '}
            <strong>
              {remainingPaymentsAmount}
              {showCcFeeBreakdown && remainingPaymentsTransactionFee ? (
                <>
                  {' plus a '}
                  {remainingPaymentsTransactionFee}
                  {' credit card fee ('}
                  {remainingPaymentsTotal}
                  {' total)'}
                </>
              ) : null}
              {' on the '}
              {nthDayFormatter(billingDayOfMonth)} {paymentFrequencyLookup[paymentType]}
              {megaDownPay ? ` starting on ${awsDateToStringDate(schedule[1].date)}` : ''}
            </strong>{' '}
            during your current policy term. You will receive an email reminder 48 hours in advance of future payments,
            and you may change your payment method at any time through your {brandConstants.SHORT_NAME} account or
            mobile app.
            <br />
            <br />
            For any policy renewals, you authorize {brandConstants.FULL_NAME} to charge your{' '}
            {paymentNameLookup[paymentType]} premium on the{' '}
            <strong>{nthDayFormatter(billingDayOfMonth)} of the month</strong> prior to your renewal policy's effective
            date to help avoid any gaps in coverage.
          </>
        )}
        {!payingInInstallments && (
          <>
            for the full amount of your current policy term. You will receive an email reminder 48 hours in advance of
            future payments, and you may change your payment method at any time through your {brandConstants.SHORT_NAME}{' '}
            account or mobile app.
            <br />
            <br />
            For any policy renewals, you authorize {brandConstants.SHORT_NAME} to charge you the full policy amount{' '}
            {daysBeforeEffectiveDateForRenewalBilling} days prior to the effective date to help avoid any gaps in
            coverage.
          </>
        )}
        {showCcFeeBreakdown && (
          <>
            <br />
            <br />
            <CreditCardFeeDisclaimer fee={parseFloat(String(transactionFee).replace(/[^0-9.-]+/g, ''))} />
          </>
        )}
      </>
    );
  }

  return (
    <>
      charge your <strong>{creditCardInfo}</strong>
      for the amount of{' '}
      <strong>
        {amount}
        {showCcFeeBreakdown && (
          <>
            {' + '}
            {transactionFee}*{' CC fees'}
          </>
        )}{' '}
        today.
      </strong>{' '}
      {payingInInstallments && (
        <>
          for the initial payment of your policy and{' '}
          <strong>
            {remainingPaymentsAmount}
            {showCcFeeBreakdown && remainingPaymentsTransactionFee ? (
              <>
                {' + '}
                {remainingPaymentsTransactionFee}
                {' CC fees, '}
                {remainingPaymentsTotal}
                {' total'}
              </>
            ) : null}
            {' on the '}
            {nthDayFormatter(billingDayOfMonth)} {paymentFrequencyLookup[paymentType]}
            {megaDownPay ? ` starting on ${awsDateToStringDate(schedule[1].date)}` : ''}
          </strong>{' '}
          for your current policy term. You will receive an email reminder 48-hours in advance of future payments and
          you can change your method of payment at any time through your {brandConstants.SHORT_NAME} account or on our
          mobile app.
          <br />
          <br />
          For any policy renewals, you authorize {brandConstants.FULL_NAME} to charge you the{' '}
          {paymentNameLookup[paymentType]} policy premium on the{' '}
          <strong>{nthDayFormatter(billingDayOfMonth)} of the month</strong> before your renewal policy's effective date
          to avoid gaps in coverage.
        </>
      )}
      {!payingInInstallments && (
        <>
          for the full amount of your current policy term. You will receive an email reminder 48-hours in advance of
          future payments, and you can change your method of payment at any time through your{' '}
          {brandConstants.SHORT_NAME} account or on our mobile app.
          <br />
          <br />
          For any policy renewals, you authorize {brandConstants.SHORT_NAME} to charge you the full policy amount{' '}
          {daysBeforeEffectiveDateForRenewalBilling} days before the effective date to avoid gaps in coverage.
        </>
      )}
      {showCcFeeBreakdown && (
        <>
          <br />
          <br />
          <CreditCardFeeDisclaimer fee={parseFloat(String(transactionFee).replace(/[^0-9.-]+/g, ''))} />
        </>
      )}
    </>
  );
};

CreditCardDisclosure.propTypes = {
  paymentType: PropTypes.string.isRequired,
  cardLast4: PropTypes.string,
  effectiveDate: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  transactionFee: PropTypes.string,
  billingDayOfMonth: PropTypes.number.isRequired,
  remainingPaymentsAmount: PropTypes.string,
  remainingPaymentsTransactionFee: PropTypes.string,
  remainingPaymentsTotal: PropTypes.string,
  megaDownPay: PropTypes.bool,
  schedule: PropTypes.array,
  canBindDirectly: PropTypes.bool,
  daysBeforeEffectiveDateForRenewalBilling: PropTypes.number
};

const ACHDisclosure = ({
  paymentType,
  amount,
  billingDayOfMonth,
  remainingPaymentsAmount,
  megaDownPay,
  schedule,
  daysBeforeEffectiveDateForRenewalBilling
}) => {
  const payingInInstallments = [PaymentTypes.QuarterTerm, PaymentTypes.HalfTerm, PaymentTypes.Monthly].includes(
    paymentType
  );

  if (useUpdatedDisclosureCopy) {
    return (
      <>
        debit your account for <strong>{amount} today</strong>{' '}
        {payingInInstallments && (
          <>
            for the initial payment of your policy, and{' '}
            <strong>
              {remainingPaymentsAmount} on the {nthDayFormatter(billingDayOfMonth)}{' '}
              {paymentFrequencyLookup[paymentType]}
              {megaDownPay ? ` starting on ${awsDateToStringDate(schedule[1].date)}` : ''}
            </strong>{' '}
            during your current policy term. You will receive an email reminder 48 hours in advance of future payments,
            and you may change your payment method at any time through your {brandConstants.SHORT_NAME} account or
            mobile app.
            <br />
            <br />
            For any policy renewals, you authorize {brandConstants.FULL_NAME} to charge your{' '}
            {paymentNameLookup[paymentType]} premium on the{' '}
            <strong>{nthDayFormatter(billingDayOfMonth)} of the month</strong> prior to your renewal policy's effective
            date to help avoid any gaps in coverage.
          </>
        )}
        {!payingInInstallments && (
          <>
            for the full amount of your current policy term. You will receive an email reminder 48 hours in advance of
            future payments, and you may change your payment method at any time through your {brandConstants.SHORT_NAME}{' '}
            account or mobile app.
            <br />
            <br />
            For any policy renewals, you authorize {brandConstants.SHORT_NAME} to charge you the full policy amount{' '}
            {daysBeforeEffectiveDateForRenewalBilling} days prior to the effective date to help avoid any gaps in
            coverage.
          </>
        )}
      </>
    );
  }

  return (
    <>
      debit your account for the amount of <strong>{amount} today.</strong>{' '}
      {payingInInstallments && (
        <>
          For the initial payment of your policy and{' '}
          <strong>
            {remainingPaymentsAmount} on the {nthDayFormatter(billingDayOfMonth)} {paymentFrequencyLookup[paymentType]}
            {megaDownPay ? ` starting on ${awsDateToStringDate(schedule[1].date)}` : ''}
          </strong>{' '}
          for your current policy term. You will receive an email reminder 48-hours in advance of future payments, and
          you can change your method of payment at any time through your {brandConstants.SHORT_NAME} account or on our
          mobile app.
          <br />
          <br />
          For any policy renewals, you authorize {brandConstants.FULL_NAME} to charge you the monthly policy premium on
          the <strong>{nthDayFormatter(billingDayOfMonth)} of the month</strong> before your renewal policy's effective
          date to avoid gaps in coverage.
        </>
      )}
      {!payingInInstallments && (
        <>
          for the full amount of your current policy term. You will receive an email reminder 48-hours in advance of
          future payments, and you can change your method of payment at any time through your{' '}
          {brandConstants.SHORT_NAME} account or on our mobile app.
          <br />
          <br />
          For any policy renewals, you authorize {brandConstants.SHORT_NAME} to charge you the full policy amount{' '}
          {daysBeforeEffectiveDateForRenewalBilling} days before the effective date to avoid gaps in coverage.
        </>
      )}
    </>
  );
};

ACHDisclosure.propTypes = {
  paymentType: PropTypes.string.isRequired,
  effectiveDate: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  billingDayOfMonth: PropTypes.number.isRequired,
  remainingPaymentsAmount: PropTypes.string,
  megaDownPay: PropTypes.bool,
  schedule: PropTypes.array,
  daysBeforeEffectiveDateForRenewalBilling: PropTypes.number
};

export const DirectDisclosure = ({
  paymentType,
  amount,
  billingDayOfMonth,
  directBillPreferredPaymentMethod,
  cardLast4,
  transactionFee,
  canBindDirectly
}) => {
  const isCcFirstInstallment = directBillPreferredPaymentMethod === PaymentMethods.CreditCard;
  const showCcFeeBreakdown = brandConfig.supportedFees?.creditCard && isCcFirstInstallment && !!transactionFee;
  const creditCardInfo = !canBindDirectly
    ? 'credit card '
    : cardLast4
      ? `credit card ending in ${cardLast4} `
      : '(Please fill credit card info) ';

  // "You authorize {brand} to" is prefixed by the parent Disclosure component — DirectDisclosure
  // emits the rest. Direct Bill only ever debits/charges the FIRST installment at bind; every
  // later installment (and renewals) is invoiced, never auto-debited. So the copy must not imply
  // recurring debits or autopay-style "change your payment method" self-service (issue #33583):
  //   OneTime      → "...for $X today as the full payment for your 12-month policy. ...billed directly..."
  //   Half/Quarter → "...for $X today as the initial payment... ...no further automatic payments... invoiced..."
  // CC swaps "debit your account" for "charge your credit card ending in {last4}", with the fee
  // breakdown ("$X + $fee* CC fee") and a footer disclaimer; otherwise mirrors the ACH copy.
  const isOneTime = paymentType === PaymentTypes.OneTime;
  const isHalfOrQuarter = [PaymentTypes.HalfTerm, PaymentTypes.QuarterTerm].includes(paymentType);

  const verbAndAmount = isCcFirstInstallment ? (
    <>
      charge your <strong>{creditCardInfo}</strong>
      for{' '}
      <strong>
        {amount}
        {showCcFeeBreakdown && (
          <>
            {' plus a '}
            {transactionFee}
            {'* CC fee'}
          </>
        )}{' '}
        today
      </strong>
    </>
  ) : (
    <>
      debit your account for <strong>{amount} today</strong>
    </>
  );

  return (
    <>
      {verbAndAmount}{' '}
      {isOneTime && (
        <>
          as the full payment for your 12-month policy. Your policy is billed directly, so{' '}
          <strong>no further automatic payments</strong> will be taken — you'll receive an{' '}
          <strong>invoice 30 days</strong> in advance of your next renewal, which you can pay through your preferred
          method.
          <br />
          <br />
          For renewals, you authorize {brandConstants.FULL_NAME} to invoice you for the full premium for your next
          policy term on the{' '}
          <strong>{nthDayFormatter(billingDayOfMonth)} of the month prior to your renewal effective date</strong> to
          help avoid any gaps in coverage.
        </>
      )}
      {isHalfOrQuarter && (
        <>
          as the initial payment for your 12-month policy. Your policy is billed directly, so{' '}
          <strong>no further automatic payments</strong> will be taken — you'll receive an{' '}
          <strong>invoice 30 days</strong> in advance of each future installment and can pay each one through your
          preferred method.
          <br />
          <br />
          For renewals, you authorize {brandConstants.FULL_NAME} to invoice you for the first installment of your next
          policy term on the{' '}
          <strong>{nthDayFormatter(billingDayOfMonth)} of the month prior to your renewal effective date</strong> to
          help avoid any gaps in coverage.
        </>
      )}
      {!isOneTime && !isHalfOrQuarter && (
        // legacy direct billing without a chosen first-installment method (or a non-supported
        // payment type)
        <>
          for your current policy term. Your policy is billed directly, so{' '}
          <strong>no further automatic payments</strong> will be taken — you'll receive invoices 30 days in advance of
          upcoming payments, which you can pay through your preferred method.
        </>
      )}
      {showCcFeeBreakdown && (
        <>
          <br />
          <br />
          <small>
            *Each credit card payment is subject to a processing fee of <strong>2.85% + $0.25</strong>{' '}
            <strong>({transactionFee})</strong>. This fee applies only to this initial installment — subsequent
            installments are billed via invoice with no processing fee.
          </small>
        </>
      )}
    </>
  );
};

DirectDisclosure.propTypes = {
  paymentType: PropTypes.string.isRequired,
  effectiveDate: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  billingDayOfMonth: PropTypes.number.isRequired,
  megaDownPay: PropTypes.bool,
  schedule: PropTypes.array,
  directBillPreferredPaymentMethod: PropTypes.string,
  cardLast4: PropTypes.string,
  transactionFee: PropTypes.string,
  canBindDirectly: PropTypes.bool,
  daysBeforeEffectiveDateForRenewalBilling: PropTypes.number
};

export const BillToOtherDisclosure = ({
  amount,
  billingDayOfMonth,
  directBillPreferredPaymentMethod,
  cardLast4,
  transactionFee,
  canBindDirectly,
  billToOtherParty
}) => {
  const interestedParty = billToOtherParty?.name || '(Please add interested party details)';
  const isCcFirstInstallment = directBillPreferredPaymentMethod === PaymentMethods.CreditCard;
  const showCcFeeBreakdown = brandConfig.supportedFees?.creditCard && isCcFirstInstallment && !!transactionFee;
  const creditCardInfo = !canBindDirectly
    ? 'credit card '
    : cardLast4
      ? `credit card ending in ${cardLast4} `
      : '(Please fill credit card info) ';

  // BillToOther is OneTime only — full annual paid at bind on the third party's CC/ACH.
  // Wording mirrors DirectDisclosure but names the third party as the payer.
  const verbAndAmount = isCcFirstInstallment ? (
    <>
      charge <strong>{interestedParty}</strong>'s <strong>{creditCardInfo}</strong>
      for{' '}
      <strong>
        {amount}
        {showCcFeeBreakdown && (
          <>
            {' plus a '}
            {transactionFee}
            {'* CC fee'}
          </>
        )}{' '}
        today
      </strong>
    </>
  ) : (
    <>
      debit <strong>{interestedParty}</strong>'s account for <strong>{amount} today</strong>
    </>
  );

  return (
    <>
      {verbAndAmount} as the full payment for your 12-month policy. Your policy is billed directly, so{' '}
      <strong>no further automatic payments</strong> will be taken — {interestedParty} will receive an{' '}
      <strong>invoice 30 days</strong> in advance of your next renewal, which they can pay through their preferred
      method.
      <br />
      <br />
      For renewals, you authorize {brandConstants.FULL_NAME} to invoice {interestedParty} for the full premium of your
      next policy term on the <strong>{nthDayFormatter(billingDayOfMonth)} of the month</strong> prior to your renewal
      effective date to help avoid any gaps in coverage.
      {showCcFeeBreakdown && (
        <>
          <br />
          <br />
          <small>
            *Each credit card payment is subject to a processing fee of <strong>2.85% + $0.25</strong>{' '}
            <strong>({transactionFee})</strong>. This is a one-time processing fee for the single annual payment —
            renewals are billed via invoice with no processing fee.
          </small>
        </>
      )}
    </>
  );
};

BillToOtherDisclosure.propTypes = {
  amount: PropTypes.string.isRequired,
  billingDayOfMonth: PropTypes.number.isRequired,
  directBillPreferredPaymentMethod: PropTypes.string,
  cardLast4: PropTypes.string,
  transactionFee: PropTypes.string,
  canBindDirectly: PropTypes.bool,
  billToOtherParty: PropTypes.object.isRequired
};

const Disclosure = ({ paymentMethod, state, ...props }) => {
  const daysPriorEffectiveDateForBilling = defaultRenewalBillingDayStates[state]
    ? 15
    : daysBeforeEffectiveDateForRenewalBilling[paymentMethod];

  return (
    <>
      You authorize {brandConstants.FULL_NAME} to{' '}
      {paymentMethod === PaymentMethods.Escrow && <MortgageDisclosure {...props} />}
      {paymentMethod === PaymentMethods.CreditCard && (
        <CreditCardDisclosure {...props} daysBeforeEffectiveDateForRenewalBilling={daysPriorEffectiveDateForBilling} />
      )}
      {paymentMethod === PaymentMethods.ACH && (
        <ACHDisclosure {...props} daysBeforeEffectiveDateForRenewalBilling={daysPriorEffectiveDateForBilling} />
      )}
      {paymentMethod === PaymentMethods.Direct && (
        <DirectDisclosure {...props} daysBeforeEffectiveDateForRenewalBilling={daysPriorEffectiveDateForBilling} />
      )}
      {paymentMethod === PaymentMethods.BillToOther && (
        <BillToOtherDisclosure {...props} daysBeforeEffectiveDateForRenewalBilling={daysPriorEffectiveDateForBilling} />
      )}
    </>
  );
};

Disclosure.propTypes = {
  paymentMethod: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired
};

const PaymentsDisclosures = ({ policyType, offer }) => {
  const classes = useStyles();
  const { values } = useFormikContext();
  const { canBindDirectly } = useCheckoutContext();
  const { hasHome, hasAuto, hasCondo, hasFarm } = getPolicyType(
    policyType,
    offer?.quote?.noBindHome,
    offer?.quote?.noBindAuto
  );

  const {
    homeownersPaymentMethod,
    homeownersPaymentType,
    homeBillingDayOfMonth,
    autoPaymentMethod,
    autoPaymentType,
    autoBillingDayOfMonth,
    rentersPaymentMethod,
    rentersPaymentType,
    rentersEffectiveDate,
    rentersBillingDayOfMonth,
    mortgageDetails,
    cardLast4,
    recoveredPaymentData,
    condoPaymentMethod,
    condoPaymentType,
    condoBillingDayOfMonth,
    condoEffectiveDate,
    autoMegaDownPay,
    homeMegaDownPay,
    rentersMegaDownPay,
    condoMegaDownPay,
    farmPaymentMethod,
    farmPaymentType,
    farmBillingDayOfMonth,
    farmEffectiveDate,
    farmMegaDownPay,
    directBillPreferredPaymentMethod
  } = values;

  const prices = usePlanPrice(offer, policyType, useDetailedPrice);

  // BillToOther parties for Farm policies live in `farmInterestedParties`. Fall back to the
  // general `interestedParties` array for non-Farm contexts (today nothing else uses BillToOther,
  // but the fallback is safe).
  const billToOtherParty =
    offer?.quote?.farmInterestedParties?.find((party) => party.relationship === 'billToOther') ||
    offer?.quote?.interestedParties?.find((party) => party.relationship === 'billToOther') ||
    false;

  const commonProps = {
    mortgageDetails,
    cardLast4: cardLast4 || recoveredPaymentData?.creditCard?.card.last4,
    canBindDirectly,
    state: offer.quote.correctedAddress.state,
    billToOtherParty
  };

  const homeProps = {
    ...commonProps,
    paymentMethod: homeownersPaymentMethod,
    paymentType: homeownersPaymentType,
    effectiveDate: dateFormatter(new Date()),
    billingDayOfMonth: homeBillingDayOfMonth,
    amount: currencyFormatter(prices.homePrice),
    remainingPaymentsAmount: prices.isHomeMonthly ? currencyFormatter(prices.homeRemainingPaymentsAmount) : '0',
    megaDownPay: homeMegaDownPay,
    schedule: prices.homeNextPayments
  };

  const autoProps = {
    ...commonProps,
    paymentMethod: autoPaymentMethod,
    paymentType: autoPaymentType,
    billingDayOfMonth: autoBillingDayOfMonth,
    effectiveDate: dateFormatter(new Date()),
    amount: currencyFormatter(prices.autoPrice),
    remainingPaymentsAmount: prices.isAutoMonthly ? currencyFormatter(prices.autoRemainingPaymentsAmount) : '0',
    megaDownPay: autoMegaDownPay,
    schedule: prices.autoNextPayments
  };

  const rentersProps = {
    ...commonProps,
    paymentMethod: rentersPaymentMethod,
    paymentType: rentersPaymentType,
    billingDayOfMonth: rentersBillingDayOfMonth,
    effectiveDate: rentersEffectiveDate,
    amount: currencyFormatter(prices.rentersPrice),
    remainingPaymentsAmount: prices.isRentersMonthly ? currencyFormatter(prices.rentersRemainingPaymentsAmount) : '0',
    megaDownPay: rentersMegaDownPay,
    schedule: prices.rentersNextPayments
  };

  const condoProps = {
    ...commonProps,
    paymentMethod: condoPaymentMethod,
    paymentType: condoPaymentType,
    billingDayOfMonth: condoBillingDayOfMonth,
    effectiveDate: condoEffectiveDate,
    amount: currencyFormatter(prices.condoPrice),
    remainingPaymentsAmount: prices.isCondoMonthly ? currencyFormatter(prices.condoRemainingPaymentsAmount) : '0',
    condoMegaDownPay,
    schedule: prices.condoNextPayments
  };

  const farmProps = {
    ...commonProps,
    paymentMethod: farmPaymentMethod,
    paymentType: farmPaymentType,
    billingDayOfMonth: farmBillingDayOfMonth,
    effectiveDate: farmEffectiveDate,
    // farmPrice is the full billed installment (CC fee included). For the "$base + $fee CC fees"
    // disclosure text we pass the derived base so the two parts add up to the displayed total.
    amount: currencyFormatter(prices.farmPriceBase ?? prices.farmPrice),
    transactionFee: prices.farmPriceTransactionFee ? currencyFormatter(prices.farmPriceTransactionFee) : undefined,
    remainingPaymentsAmount: prices.isFarmMonthly
      ? currencyFormatter(prices.farmRemainingPaymentsBase ?? prices.farmRemainingPaymentsAmount)
      : '0',
    remainingPaymentsTransactionFee: prices.farmRemainingPaymentsTransactionFee
      ? currencyFormatter(prices.farmRemainingPaymentsTransactionFee)
      : undefined,
    remainingPaymentsTotal: prices.farmRemainingPaymentsAmount
      ? currencyFormatter(prices.farmRemainingPaymentsAmount)
      : undefined,
    megaDownPay: farmMegaDownPay,
    schedule: prices.farmNextPayments,
    directBillPreferredPaymentMethod
  };

  const disclosureMapper = (type) => {
    switch (type) {
      case PolicyType.Home: {
        if (hasHome) {
          return (
            <li key={type}>
              For your home policy, <Disclosure {...homeProps} />
            </li>
          );
        }
        break;
      }
      case PolicyType.Auto: {
        if (hasAuto) {
          return (
            <li key={type}>
              For your auto policy, <Disclosure {...autoProps} />
            </li>
          );
        }
        break;
      }
      case PolicyType.Renters: {
        if (standaloneRentersStates[offer.quote.correctedAddress.state]) {
          return (
            <li key={type}>
              For your renters policy, <Disclosure {...rentersProps} />
            </li>
          );
        }
        break;
      }
      case PolicyType.Condo: {
        if (hasCondo) {
          return (
            <li key={type}>
              For your condo policy, <Disclosure {...condoProps} />
            </li>
          );
        }
        break;
      }
      case PolicyType.Farm:
      case PolicyType.FarmHome:
      case PolicyType.FarmRental: {
        if (hasFarm) {
          return (
            <li key={type}>
              <Disclosure {...farmProps} />
            </li>
          );
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <Section title="Payments Disclosures" type="SubSection">
      <Card type="primary">
        <Grid container item xs={12} className={classes.container}>
          <Label type="greenSmall" className={classes.title}>
            Please go through these carefully with the potential member
          </Label>
          <ul className={classes.list}>{policyType.split('').map(disclosureMapper)}</ul>
        </Grid>
        <Grid item xs={12} className={classes.footer}>
          <FormField
            name="authorizePayments"
            type="checkbox"
            label="Insured member authorizes these payments"
            mode="light"
            fast={false}
          />
        </Grid>
      </Card>
    </Section>
  );
};

PaymentsDisclosures.propTypes = {
  policyType: PropTypes.string.isRequired,
  offer: PropTypes.object.isRequired
};

export default PaymentsDisclosures;
